import EventNames from '../../config/eventNames';
import { getServices } from '../../infra/startup';

import authV2 from '../v2/auth';
import initializeABTestingInterface from './ABTesting';
import initializeAccessControlInterface from './accessControl';
import initializeAnalyticsInterface from './analytics';
import initializeAppInterfaceV1 from './app';
import initializeAuthProviderInterface from './auth';
import Breadcrumb from './breadcrumb';
import initializeCriterionSingleton from './Criterion/singleton';
import EventInterface from './events';
import Fallback from './Fallback';
import initializeFeatureFlagInterfaceV1 from './featureFlags';
import initializeGrantsInterfaceV1 from './grants';
import initializeGrantsHistoryInterfaceV1 from './grants/grantsHistory';
import initializeGraphQLInterfaceV1 from './graphql';
import initializeLocalizationInterface from './localization';
import initializeMFERouterInterface from './mfeRouter';
import initializeMonitoringInterfaceV1 from './monitoring';
import initializeNavigationInterface from './navigation';
import initializeLoggerInterface from './clientOsLogger';
import optimizely from './optimizely';
import OrgSelector from './orgSelector';
import initializeRoutesInterface from './routes';
import serviceRoutingImport from './serviceRouting';
import initializeServiceWorkerInterface from './serviceWorker';
import initializeSessionInterfaceV1 from './sessionInterface';
import Store from './store';
import initializeTenantHandlerInterfaceSingleton from './TenantHandler';
import initializeThemeInterface from './theme';
import { UserSessionInterfaceType } from './types';
import { initializeUserActivityInterface } from './userActivity';
import initializeUserInterfaceV1 from './userInterface';
import {
  StorageLocale,
  localeSessionRepository
} from '../../behaviors/afterInitBehaviors/storageLocale';
import InitializeDeprecatedServices from '../../behaviors/afterInitBehaviors/initializeDeprecatedServices';
import { container } from 'tsyringe';
import type { APIv1Type, EventsInterfaceType } from '@clientos/types';
import AuthenticationProviderEnum from '../../config/authenticationProviderEnum';
import initializeEntitlementsInterface from './entitlements';
import { bootstrapManager } from '../../infra/startup/bootstrapManager';
import {
  AuthProvidersPropsType,
  CommonsInitializeType
} from '../../infra/startup/types';

export default async function initializeShellInterfaceV1(
  commonsInitializeInterfaces: CommonsInitializeType
): Promise<APIv1Type> {
  const services = getServices();
  const {
    applicationService,
    authTokenService,
    breadcrumbService,
    featureFlagService,
    mfeRouterService,
    sessionService,
    loginService,
    tenantHandlerService,
    supportSessionService,
    userService,
    localizationService,
    navigationService,
    eventService
  } = services;

  const {
    accessControl: accessControlInput,
    adapter: adapterInput,
    analytics: analyticsInput,
    breadcrumb: breadcrumbInput,
    criterions: criterionsInput,
    entitlements: entitlementsInput,
    fallbackConfiguration,
    localization: localizationInput,
    login: loginInput,
    navigation: navigationInput,
    onboarding: onboardingInput,
    userOnboarding: userOnboardingInput,
    optimizely: optimizelyInput,
    stack,
    basePath
  } = commonsInitializeInterfaces;

  /** Integration phase
   * - Cross-service integration
   */
  bootstrapManager.setIntegration();

  //Initializing auth provider
  const authProvider = await initializeAuthProviderInterface();

  // TODO: Should get its service via getServices method.
  const appInterface = await initializeAppInterfaceV1({
    applicationService
  });

  const userActivityInterface = await initializeUserActivityInterface();

  const abTestingInterface = await initializeABTestingInterface();

  const analyticsInterface = await initializeAnalyticsInterface({
    isEnabled: analyticsInput.enabled
  });

  const navigationInterface = await initializeNavigationInterface({
    useLegacyNavigation: navigationInput.useLegacyNavigation
  });

  const localizationInterface = await initializeLocalizationInterface();

  const accessControlInterface = await initializeAccessControlInterface({
    accessControl: accessControlInput
  });

  const featureFlagsInterface = await initializeFeatureFlagInterfaceV1({
    featureFlagService
  });

  const newLoggerInterface = await initializeLoggerInterface();

  const grantsInterface = await initializeGrantsInterfaceV1();

  const grantsHistoryInterface = await initializeGrantsHistoryInterfaceV1();

  const userInterface = await initializeUserInterfaceV1();

  const sessionInterface = await initializeSessionInterfaceV1();

  const graphQLInterface = await initializeGraphQLInterfaceV1();

  const monitoringInterface = await initializeMonitoringInterfaceV1();

  const themeInterface = await initializeThemeInterface();

  /** Below here we have some pattern issues.
   *
   */
  bootstrapManager.setNonCanonicalInitialization();

  //Initializing other interfaces

  const eventsInterface =
    new EventInterface() as unknown as EventsInterfaceType;

  const storeInterface = new Store();

  optimizely(optimizelyInput);

  // TODO we'll remove all this code when interfaces won't be necessary anymore.
  services?.analyticsService?.setInterfaceDependencies({
    authProvider,
    store: storeInterface
  });
  // TODO: this shouldn't be here, should be in Phrase 4 startup
  await services?.analyticsService?.init();
  await services?.consentService?.init(); // after  navigation
  await services.abTestingService.init(); // after consents

  // Initialize deprecated auth v2
  const authProviderV2 = authV2(loginInput);
  authProviderV2._setSessionService(sessionService);
  authProviderV2._setTenantHandlerService(tenantHandlerService);
  authProviderV2._setLoginService(loginService);

  const entitlementsInterface = await initializeEntitlementsInterface({
    authProvider: authProviderV2,
    entitlementsInput,
    events: eventsInterface
  });

  const orgSelector = await OrgSelector({
    onboarding: onboardingInput,
    authProvider: authProviderV2.createOrgedAuthProvider(),
    orglessAuthProvider: authProviderV2.createOrglessAuthProvider(),
    navigation: navigationInterface,
    localization: localizationInterface,
    sessionService,
    tenantHandlerService,
    userService
  });

  const serviceRouting = serviceRoutingImport({
    onboardingInput
    // store: storeInterface
  });

  const criterionInterface = initializeCriterionSingleton({
    interfaces: {
      accessControl: accessControlInterface,
      entitlements: entitlementsInterface,
      grantsHistory: grantsHistoryInterface,
      grants: grantsInterface
    },
    services: {
      eventService,
      sessionService,
      tenantHandler: tenantHandlerService,
      featureFlagService,
      userService,
      localization: localizationService
    },
    criterions: criterionsInput
  });

  // TODO we'll remove all this code when interfaces won't be necessary anymore.
  services?.routesService?.setInterfaceDependencies({ criterionInterface });

  // My desire is to remove that, but unfortunally is been used for other MFEs.
  const userSession: UserSessionInterfaceType = {
    redirectToLogin: loginService.redirectToLogin,
    getLoginPath: loginService.getLoginPath
  };

  const fallbackData = {
    event: {
      eventService: eventService,
      eventName: EventNames.globalFallback
    },
    navigationInterface: navigationInterface,
    configuration: fallbackConfiguration
  };
  const fallback = new Fallback(fallbackData);

  await breadcrumbService.start({
    enable: breadcrumbInput?.enable,
    settingsList: breadcrumbInput?.settingsList,
    defaultSettingsKey: breadcrumbInput?.defaultSettingsKey,
    navigation: navigationInterface,
    criterion: criterionInterface
  });

  const breadcrumbsInterface = new Breadcrumb();

  /**
   * Starting MFE Router below
   */
  // TODO we'll remove all this code when interfaces won't be necessary anymore.
  mfeRouterService.setInterfaceDependencies({
    interfaces: {
      store: storeInterface,
      navigation: navigationInterface,
      fallback,
      authProvider,
      localization: localizationInterface,
      criterion: criterionInterface
    }
  });

  const isNative: boolean = container.resolve('IsNative');

  const authProviderProps: AuthProvidersPropsType =
    container.resolve('AuthProviderProps');
  /***
   * TODO: Workaround to fix login loop on /activate.
   * Need to refactor since this is an afterInitBehavior.
   */
  if (
    authProviderProps?.authenticationProvider ===
    AuthenticationProviderEnum?.authz
  )
    await InitializeDeprecatedServices?.init?.({
      sessionService,
      orgSelector,
      isNative
    });

  // Initialize StorageLocale before router initialization to ensure locale is preserved after login.
  !isNative &&
    (await StorageLocale?.init?.({
      navigationService,
      localizationService,
      localeSessionRepository
    }));

  // TODO: merge Start and Init.
  if (!adapterInput?.disableRouter) {
    await mfeRouterService.init({
      localization: localizationInput,
      onboarding: onboardingInput,
      login: loginInput,
      userOnboarding: userOnboardingInput,
      basePath: basePath,
      stack: stack
    });
    await mfeRouterService.start();
  }

  // Now, we start the MFERouterInterface.
  const microfrontendRouter = await initializeMFERouterInterface({
    isEnabled: !adapterInput?.disableRouter
  });
  /**
   * Bootstrapping mfeRouter  finished.
   */

  const routesInterface = await initializeRoutesInterface();

  const tenantHandlerInterface =
    await initializeTenantHandlerInterfaceSingleton();

  const serviceWorkerInterface = await initializeServiceWorkerInterface();

  return {
    app: appInterface,
    abTesting: abTestingInterface,
    userInterface,
    sessionInterface,
    sessionService,
    authProvider,
    analytics: analyticsInterface,
    events: eventsInterface,
    navigation: navigationInterface,
    store: storeInterface,
    featureFlags: featureFlagsInterface,
    localization: localizationInterface,
    orgSelector,
    theme: themeInterface,
    serviceRouting,
    logger: newLoggerInterface,
    userActivity: userActivityInterface,
    entitlements: entitlementsInterface,
    accessControl: accessControlInterface,
    criterion: criterionInterface,
    userSession,
    fallback,
    microfrontendRouter,
    tenantHandlerInterface,
    tenantHandler: tenantHandlerService,
    authToken: authTokenService,
    breadcrumbs: breadcrumbsInterface,
    routes: routesInterface,
    supportSessionService,
    monitoring: monitoringInterface,
    serviceWorker: serviceWorkerInterface,
    userService,
    grants: grantsInterface,
    grantsHistory: grantsHistoryInterface,
    graphql: graphQLInterface
  };
}
